import { MARQUEE_BASE_DURATION, MARQUEE_MIN_DURATION } from './constants.js';

/**
 * Calculate cubic bezier timing function for marquee animation
 * @param {number} duration - Animation duration in seconds
 * @returns {string} - CSS cubic-bezier timing function
 */
export const calculateCubicBezier = (duration) => {
	try {
		// Input validation with fallbacks
		if (!duration || typeof duration !== 'number' || !isFinite(duration) || duration <= 0) {
			console.warn('Invalid duration for marquee animation, using fallback');
			return 'ease-in-out'; // Safe CSS fallback
		}

		const ease = 0.75; 
		const baseDuration = 2.0;

		const scale = baseDuration / duration;
		const clamp = (v) => Math.min(Math.max(v, 0), 0.5);

		const x1 = clamp(ease * scale);
		const x2 = 1 - clamp((ease * scale));
		
		// Validate bezier values before returning
		if (!isFinite(x1) || !isFinite(x2) || x1 < 0 || x1 > 1 || x2 < 0 || x2 > 1) {
			console.warn('Invalid bezier values calculated, using fallback');
			return 'ease-in-out';
		}
		
		return `cubic-bezier(${x1.toFixed(3)}, 0.0, ${x2.toFixed(3)}, 1.0)`;
	} catch (error) {
		console.error('Error calculating cubic bezier for marquee:', error);
		return 'ease-in-out'; // Always return a valid CSS value
	}
};

/**
 * Check if text element needs marquee animation and apply CSS variables
 * @param {HTMLElement} textElement - The text element to check
 * @param {HTMLElement} container - The container element
 */
export const setupMarqueeAnimation = (textElement, container) => {
	try {
		// Early validation to prevent errors
		if (!textElement || !container) {
			return;
		}

		// Ensure elements are actually in the DOM
		if (!textElement.isConnected || !container.isConnected) {
			return;
		}

		// Safe property access with fallbacks
		const textWidth = textElement.scrollWidth || 0;
		const containerWidth = container.clientWidth || 0;
		
		// Safe computed style access
		let containerPaddingLeft = 0;
		let containerPaddingRight = 0;
		
		try {
			const computedStyle = getComputedStyle(container);
			containerPaddingLeft = parseInt(computedStyle.paddingLeft) || 0;
			containerPaddingRight = parseInt(computedStyle.paddingRight) || 0;
		} catch (styleError) {
			console.warn('Could not access container computed styles:', styleError);
		}

		// Only proceed if we have valid dimensions
		if (textWidth <= 0 || containerWidth <= 0) {
			textElement.classList.remove('needs-marquee');
			return;
		}
		
		if (textWidth > containerWidth) {
			const overflowWidth = textWidth - containerWidth;
			
			// Ensure we have valid constants before calculation
			const minDuration = MARQUEE_MIN_DURATION || 1.0;
			const baseDuration = MARQUEE_BASE_DURATION || 0.05;
			
			const duration = Math.max(minDuration, overflowWidth * baseDuration / 10);
			const timingFunction = calculateCubicBezier(duration);
			
			// Safe CSS property setting
			try {
				textElement.classList.add('needs-marquee');
				textElement.style.setProperty('--variable-width', `${overflowWidth + containerPaddingLeft + containerPaddingRight}px`);
				textElement.style.setProperty('--animation-duration', `${duration.toFixed(2)}s`);
				textElement.style.setProperty('--animation-timing-function', timingFunction);
			} catch (cssError) {
				console.error('Error setting marquee CSS properties:', cssError);
				// Clean up if CSS setting fails
				textElement.classList.remove('needs-marquee');
			}
		} else {
			// Clean up existing marquee if text no longer overflows
			textElement.classList.remove('needs-marquee');
			textElement.style.removeProperty('--variable-width');
			textElement.style.removeProperty('--animation-duration');
			textElement.style.removeProperty('--animation-timing-function');
		}
	} catch (error) {
		console.error('Error in setupMarqueeAnimation:', error);
		// Ensure clean state on any error
		try {
			if (textElement) {
				textElement.classList.remove('needs-marquee');
			}
		} catch (cleanupError) {
			console.error('Error during marquee cleanup:', cleanupError);
		}
	}
};
